import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import MetabolismPatientForm from "../components/MetabolismPatienForm";
import {
  FaDownload,
  FaCalendarAlt,
  FaFileAlt,
  FaStopwatch,
  FaFlask,
  FaTachometerAlt,
  FaHistory,
} from "react-icons/fa";
import PatientTypeSelector from "../components/PatientTypeSelector";
import SearchBar from "../components/SearchBar";
import FormField from "../components/FormField";

function MetabolismForm({ name }) {
  // Generate a unique patient ID when component loads
  const generatePatientId = () => `PAT-${Date.now()}`;

  const [id, setId] = useState(generatePatientId()); // Initialize with a generated ID
  const [patientname, setName] = useState("");
  const [age, setAge] = useState("");
  const [culturalBackground, setCulturalBackground] = useState("");
  const [durationOfDiabetes, setDurationOfDiabetes] = useState("");
  const [reasonForConsultation, setReasonForConsultation] = useState("");
  const [historicalGlycemic, setHistoricalGlycemic] = useState("");
  const [fpg, setFpg] = useState(""); // Define FPG state
  const [ppg, setPpg] = useState(""); // Define PPG state
  const [hba1c, setHba1c] = useState(""); // Define HbA1c state
  const [targetGoal, setTargetGoal] = useState(""); // Define targetGoal state
  const [dateOfVisit, setDateOfVisit] = useState(""); // Define dateOfVisit state
  const [patientType, setPatientType] = useState("new"); // Define patientType state
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false); // State variable for showing success message
  const [oldFpg, setOldFpg] = useState(fpg); // Define old FPG state
  const [oldPpg, setOldPpg] = useState(ppg); // Define old PPG state
  const [oldHba1c, setOldHba1c] = useState(hba1c); // Define old HbA1c
  const [olddateOfVisit, setOldDateOfVisit] = useState(dateOfVisit); // Define oldDateOfVisit state
  const [transcript, setTranscript] = useState("");
  const [duration, setDuration] = useState("");
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [positiveWords, setPositiveWords] = useState([]);
  const [graphImage, setGraphImage] = useState("");
  const [showPreviousMeasurements, setShowPreviousMeasurements] = useState(false);

  const endOfScreenRef = useRef(null);
  const navigate = useNavigate();
  const highlightPositiveWords = (text) => {
    if (!text) return text;
    return text.split(" ").map((word, index) => {
      const wordLowerCase = word.toLowerCase();
      const isPositive = positiveWords.includes(wordLowerCase);

      return isPositive ? (
        <span key={index} className="highlight">
          {word}{" "}
        </span>
      ) : (
        <span key={index}>{word} </span>
      );
    });
  };
  const fetchPatientById = async (id) => {
    try {
      const response = await axios.get(
        `https://testone.conversationaily.ai/api/diabetes/get_patient_data/${id}`
      );
      const transcriptResponse = await axios.get(
        `https://testone.conversationaily.ai/api/diabetes/get_transcription/${id}`
      );
      // const response = await axios.get(
      //   `https://testone.conversationaily.ai/api/get_patient_by_id?patient_id=${id}`
      // );
      // const transcriptResponse = await axios.get(
      //   `https://testone.conversationaily.ai/api/get_transcript_by_patient_id?patient_id=${id}`
      // );


      const patientData = response.data;
      const transcriptData = transcriptResponse.data;
      if (!patientData || Object.keys(patientData).length === 0) {
        // If patientData is empty, show an error message
        setId("");
        setName("");
        setAge("");
        setCulturalBackground("");
        setDurationOfDiabetes("");
        setReasonForConsultation("");
        setHistoricalGlycemic("");
        setFpg("");
        setPpg("");
        setHba1c("");
        setTranscript("");
        setDuration("");
        setOldFpg("");
        setOldPpg("");
        setOldHba1c("");
        setDateOfVisit("");
        setShowErrorMessage(true);
        
        return;
      }
      setId(patientData.patient_id);
      setName(patientData.patient_name);
      setAge(patientData.age);
      setCulturalBackground(patientData.cultural_background);
      setDurationOfDiabetes(patientData.duration_of_diabetes);
      setReasonForConsultation(patientData.reason_for_consultation);
      setHistoricalGlycemic(patientData.historical_glycemic);
      setFpg(patientData.fpg);
      setPpg(patientData.ppg);
      setHba1c(patientData.hba1c);
      setDateOfVisit(patientData.date_of_visit);
      setTranscript(transcriptData.transcription);
      setDuration(transcriptData.duration);
      setPositiveWords(patientData.positive_words.split(", "));
      setGraphImage(patientData.show_graph_image);

      if (endOfScreenRef.current) {
        endOfScreenRef.current.scrollIntoView({ behavior: "smooth" });
      }
      let lastVisitDataFPG = null;
      let lastVisitDataPPG = null;
      let lastVisitDataHbA1c = null;

      lastVisitDataFPG = patientData.fpg;
      lastVisitDataPPG = patientData.ppg;
      lastVisitDataHbA1c = patientData.hba1c;


      if (patientData.visit_history && patientData.visit_history.length < 2) {
        setShowPreviousMeasurements(true);
        setOldFpg(patientData.visit_history[0]?.fpg || '');
        setOldPpg(patientData.visit_history[0]?.ppg || '');
        setOldHba1c(patientData.visit_history[0]?.hba1c || '');
        setOldDateOfVisit(patientData.visit_history[0]?.date_of_visit || '');
      } else {
        setShowPreviousMeasurements(false);
        setGraphImage(patientData.show_graph_image);
    
      }

      setShowErrorMessage(false); // Reset the error message
    } catch (error) {
      setShowErrorMessage(true); // Set the error message to true
    }
  };
  const updateGraph = async (newFpg, newPpg, newHba1c) => {
    try {
      const formData = new FormData();
      formData.append('new_fpg', newFpg);
      formData.append('new_ppg', newPpg);
      formData.append('new_hba1c', newHba1c);
  
      const response = await axios.post(
        `https://testone.conversationaily.ai/api/diabetes/compare_patient_data/${id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      setGraphImage(response.data.comparison_image);
    } catch (error) {
      console.error("Error updating graph:", error);
    }
  };
  
  const handleFpgChange = (e) => {
    const newFpg = e.target.value;
    setFpg(newFpg);
    updateGraph(newFpg, ppg, hba1c);
  };
  
  const handlePpgChange = (e) => {
    const newPpg = e.target.value;
    setPpg(newPpg);
    updateGraph(fpg, newPpg, hba1c);
  };
  
  const handleHba1cChange = (e) => {
    const newHba1c = e.target.value;
    setHba1c(newHba1c);
    updateGraph(fpg, ppg, newHba1c);
  };
  const handleSearch = (e) => {
    const inputValue = e.target.value;
    setId(inputValue);
    if (inputValue.trim() !== "") {
      fetchPatientById(inputValue);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
  
    try {
      setIsLoading(true);
  
      if (patientType === "recurring") {
        // Save the updated data first
        await axios.post(
          `https://testone.conversationaily.ai/api/diabetes/save_patient_data`,
          {
            id: id,
            patientname: patientname,
            age: age,
            cultural_background: culturalBackground,
            duration_of_diabetes: durationOfDiabetes,
            reason_for_consultation: reasonForConsultation,
            historical_glycemic: historicalGlycemic,
            fpg: fpg,
            ppg: ppg,
            hba1c: hba1c,
            date_of_visit: dateOfVisit,
          }
        );
  
        // Then fetch the updated patient data
        await fetchPatientById(id);
      } else {
        // For new patients, save the data as before
        await axios.post(
          `https://testone.conversationaily.ai/api/diabetes/save_patient_data`,
          {
            id: id,
            patientname: patientname,
            age: age,
            cultural_background: culturalBackground,
            duration_of_diabetes: durationOfDiabetes,
            reason_for_consultation: reasonForConsultation,
            historical_glycemic: historicalGlycemic,
            fpg: fpg,
            ppg: ppg,
            hba1c: hba1c,
            date_of_visit: dateOfVisit,
          }
        );
      }
  
      setIsLoading(false);
      navigate("/metabolismwordcloud", {
        state: {
          age: age,
          cultural_background: culturalBackground,
          duration_of_diabetes: durationOfDiabetes,
          reason_for_consultation: reasonForConsultation,
          historical_glycemic: historicalGlycemic,
          fpg: fpg,
          ppg: ppg,
          hba1c: hba1c,
          patientId: id,
        },
      });
    } catch (error) {
      setIsLoading(false);
      console.error("Error saving patient data:", error);
      // Optionally, you can show an error message to the user
    }
  };
  // const handleSave = async () => {
  //   try {
  //     setIsLoading(true);
  //     const response = await axios.post(
  //       `https://testone.conversationaily.ai/api/diabetes/save_patient_data`,
  //       // "https://testone.conversationaily.ai/api/save_patient_data",

  //       {
  //         id: id,
  //         patientname: patientname,
  //         age: age,
  //         cultural_background: culturalBackground,
  //         duration_of_diabetes: durationOfDiabetes,
  //         reason_for_consultation: reasonForConsultation,
  //         historical_glycemic: historicalGlycemic,
  //         fpg: fpg,
  //         ppg: ppg,
  //         hba1c: hba1c,
  //         date_of_visit: dateOfVisit,
  //       }
  //     );
  //     setIsLoading(false);
  //     setShowSuccessMessage(true); // Set success message visibility to true
  //     console.log("Patient data saved successfully:", response.data);
  //   } catch (error) {
  //     setIsLoading(false);
  //     console.error("Error saving patient data:", error);
  //     // Optionally, you can show an error message to the user
  //   }
  // };
  useEffect(() => {
    const currentDate = new Date().toISOString().split("T")[0];

    if (patientType === "recurring" && id) {
      // If it's a recurring patient and an ID is provided, set dateOfVisit to today's date
      setDateOfVisit(currentDate);
    } else if (patientType === "new") {
      // If it's a new patient, set dateOfVisit to today's date
      setDateOfVisit(currentDate);
    }
  }, [patientType, id]);
  const downloadAllPatients = async () => {
    try {
      const response = await axios.get(
        `https://testone.conversationaily.ai/api/diabetes/download_all_patients`,
        // "https://testone.conversationaily.ai/api/download_all_patients",

        { responseType: "blob" }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "all_patients.csv");

      document.body.appendChild(link);
      link.click();

      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading all patients:", error);
    }
  };

  useEffect(() => {
    // Clear the stored word cloud data from localStorage when the component unmounts
    return () => {
      localStorage.removeItem("wordCloudData");
    };
  }, [navigate]);

  useEffect(() => {
    if (patientType === "new") {
      setId(generatePatientId()); // Regenerate patient ID if a new patient is selected
    }
  }, [patientType]);

  return (
    
      <div className="flex flex-col min-h-screen">
        <div className="flex-1 z-10">
        <Navbar name={name} />
        </div>


        <div className="relative isolate px-6  lg:px-8">
          <div
            className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80"
            aria-hidden="true"
          >
            <div
              className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-[#ff80b5] to-[#9089fc] opacity-30 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]"
              style={{
                clipPath:
                  "polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
              }}
            />
          </div>
          <div className="mx-auto max-w-5xl  ">
            <div className="text-center">
              <div className="flex-grow container mx-auto px-4 py-8 flex flex-col items-center justify-center">
                {" "}
                <div className="w-full max-w-5xl rounded-lg shadow-2xl overflow-hidden mt-6">
                  <div className="p-4 sm:p-8 space-y-6 sm:space-y-8">
                    <h1 className="text-4xl font-bold text-center text-orange-500">
                      Patient Information
                    </h1>
                    <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0 sm:space-x-4">
                      <PatientTypeSelector
                        patientType={patientType}
                        setPatientType={setPatientType}
                      />
                      <button
                        className="w-full sm:w-auto px-4 py-2 bg-green-500 text-white rounded-full hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors duration-300 flex items-center justify-center"
                        onClick={downloadAllPatients}
                      >
                        <FaDownload className="mr-2" />
                        Download All
                      </button>
                    </div>
                    {showSuccessMessage && (
                      <div
                        className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 rounded"
                        role="alert"
                      >
                        <p className="font-bold">Success!</p>
                        <p>Patient data saved successfully.</p>
                      </div>
                    )}
                    <form onSubmit={handleSubmit} className="space-y-6">
                    {patientType === "recurring" ? (
  <div className="rounded-lg p-4 md:p-8 space-y-6">
    <SearchBar id={id} handleSearch={handleSearch} />
    {showErrorMessage && (
      <div
        className="bg-red-100 border-l-4 border-red-500 text-red-700 p-2 md:p-4 rounded-lg"
        role="alert"
      >
        <p className="font-bold">Error</p>
        <p>No patient details found for the provided ID.</p>
      </div>
    )}
    {id && !showErrorMessage && (
      <div className="space-y-6">
        <h2 className="text-2xl md:text-3xl font-bold text-center text-blue-800 mb-4 md:mb-6">
          Patient Details
        </h2>
        <div className="grid grid-cols-1 gap-4">
          {/* Patient ID */}
          <div className="hidden">
            <label className="block text-gray-700 font-bold mb-2">Patient ID</label>
            <input
              type="text"
              id="patientid"
              name="id"
              className="w-full px-3 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-600"
              value={id}
              onChange={(e) => e.preventDefault()}
              readOnly
              disabled
            />
          </div>
          {/* Patient Name */}
          <div className="hidden">
            <label className="block text-gray-700 font-bold mb-2">Patient Name</label>
            <input
              type="text"
              id="patientname"
              name="patientname"
              className="w-full px-3 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-600"
              value={patientname}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          {/* Age */}
          <div className="hidden">
            <label className="block text-gray-700 font-bold mb-2">Age</label>
            <input
              type="text"
              id="age"
              name="age"
              className="w-full px-3 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-600"
              value={age}
              onChange={(e) => setAge(e.target.value)}
            />
          </div>
          {/* Cultural Background */}
          <div className="hidden">
            <label
              htmlFor="cultural-background"
              className="block text-gray-700 font-bold mb-2"
            >
              Patient Cultural Background
            </label>
            <select
              id="cultural-background"
              name="cultural_background"
              className="w-full px-3 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-600"
              value={culturalBackground}
              onChange={(e) => setCulturalBackground(e.target.value)}
            >
              <option value="">Select Cultural Background</option>
              <option value="1">Vegetarian</option>
              <option value="2">Non-vegetarian</option>
              <option value="3">Vegan</option>
              <option value="4">Eggetarian</option>
            </select>
          </div>
          {/* Duration of Diabetes */}
          <div className="hidden">
            <label className="block text-gray-700 font-bold mb-2">Duration of Diabetes</label>
            <input
              type="text"
              id="duration-of-diabetes"
              name="duration_of_diabetes"
              className="w-full px-3 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-600"
              value={durationOfDiabetes}
              onChange={(e) => setDurationOfDiabetes(e.target.value)}
            />
          </div>
          {/* Reason For Consultation */}
          <div className="hidden">
            <label className="block text-gray-700 font-bold mb-2">Reason For Consultation</label>
            <select
              id="disposition"
              name="reason_for_consultation"
              className="w-full px-3 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-600"
              value={reasonForConsultation}
              onChange={(e) => setReasonForConsultation(e.target.value)}
            >
              <option value="">Select Reason For Consultation</option>
              <option value="Diagnosis-Confirmation">Diagnosis Confirmation</option>
              <option value="Blood-Glucose-Management">Blood Glucose Management</option>
              <option value="Self-Management">Self Management</option>
              <option value="Complication-Screening-and-Management">Complication Screening and Management</option>
              <option value="Gestational-Diabetes">Gestational Diabetes</option>
              <option value="Medication-Adjustments">Medication Adjustments</option>
            </select>
          </div>
          <div className="bg-gray-50 p-4 md:p-6 rounded-lg shadow-inner">
            <label className="text-gray-700 font-semibold mb-3 flex items-center">
              <FaFileAlt className="mr-2 text-blue-600" />
              Last Visit Recap
            </label>
            <p className="text-sm text-gray-500 mb-2 flex items-center">
              <FaStopwatch className="mr-2 text-blue-600" />
              Duration: {duration} seconds
            </p>
            <div className="w-full text-left px-2 py-3 bg-white rounded-lg border-2 border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 resize-none">
              {highlightPositiveWords(transcript)}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField
              icon={<FaCalendarAlt />}
              label="Date of Visit"
              type="date"
              id="dateofvisit"
              value={dateOfVisit}
              readOnly
            />
            <FormField
              icon={<FaHistory />}
              label="Rate Glycemic Control"
              type="select"
              id="historical-glycemic"
              value={historicalGlycemic}
              onChange={(e) => setHistoricalGlycemic(e.target.value)}
            >
              <option value="">Select Historical Glycemic</option>
              <option value="Fair">Fair</option>
              <option value="Good">Good</option>
              <option value="Poor">Poor</option>
              <option value="Very Poor">Very Poor</option>
            </FormField>
          </div>

          {graphImage ? (
            <div className="p-4 md:p-6 rounded-lg shadow-inner">
              <img
                src={`data:image/png;base64,${graphImage}`}
                alt="comparison"
                className="w-full"
              />
            </div>
          ) : (
            <div>No comparison data available</div>
          )}
          <div
            ref={endOfScreenRef}
            className="bg-green-100 p-4 md:p-6 rounded-lg shadow-inner"
          >
            <h3 className="text-lg md:text-xl font-semibold mb-4 text-green-800">
              New Measurements
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                icon={<FaFlask />}
                label="FPG"
                type="text"
                id="fpg"
                onChange={handleFpgChange}
                placeholder="FPG"
                required
              />
              <FormField
                icon={<FaFlask />}
                label="PPG"
                type="text"
                id="ppg"
                onChange={handlePpgChange}
                placeholder="PPG"
                required
              />
              <FormField
                icon={<FaTachometerAlt />}
                label="HbA1c"
                type="text"
                id="hba1c"
                onChange={handleHba1cChange}
                placeholder="HbA1c"
                required
              />
            </div>
          </div>
        </div>
      </div>
    )}
  </div>
) : (
                        <MetabolismPatientForm
                          id={id}
                          setId={setId}
                          patientname={patientname}
                          setName={setName}
                          age={age}
                          setAge={setAge}
                          culturalBackground={culturalBackground}
                          setCulturalBackground={setCulturalBackground}
                          durationOfDiabetes={durationOfDiabetes}
                          setDurationOfDiabetes={setDurationOfDiabetes}
                          reasonForConsultation={reasonForConsultation}
                          setReasonForConsultation={setReasonForConsultation}
                          historicalGlycemic={historicalGlycemic}
                          setHistoricalGlycemic={setHistoricalGlycemic}
                          fpg={fpg}
                          setFpg={setFpg}
                          ppg={ppg}
                          setPpg={setPpg}
                          hba1c={hba1c}
                          setHba1c={setHba1c}
                          targetGoal={targetGoal}
                          setTargetGoal={setTargetGoal}
                          dateOfVisit={dateOfVisit}
                          setDateOfVisit={setDateOfVisit}
                        />
                      )}
                      <div className="flex justify-center">
                        <button
                          type="submit"
                          className="px-8 py-3 bg-custom-gradient text-white font-semibold rounded-full hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-300 transform hover:scale-105"
                          disabled={isLoading}
                        >
                          {isLoading
                            ? "Loading..."
                            : "Generate Conversation Cues"}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>{" "}
            </div>
          </div>
          <div
            className="absolute inset-x-0 top-[calc(100%-13rem)] -z-10 transform-gpu overflow-hidden blur-3xl sm:top-[calc(100%-50rem)]"
            aria-hidden="true"
          >
            <div
              className="relative left-[calc(50%+3rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 bg-gradient-to-tr from-[#ff80b5] to-[#9089fc] opacity-30 sm:left-[calc(50%+36rem)] sm:w-[72.1875rem]"
              style={{
                clipPath:
                  "polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
              }}
            />
          </div>
        </div>
      </div>
  );
}

export default MetabolismForm;
